/**
 * Events Dashboard -- list all events for the current organizer
 */

import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  CalendarDays,
  Users,
  Video,
  Loader2,
  ArrowUpDown,
  Search,
  Trash2,
  Eye,
} from "lucide-react";
import { listEvents, deleteEvent } from "@/lib/api";
import type { MixEvent } from "@/lib/store";
import { cn, formatDateRange } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

const statusConfig: Record<
  string,
  { label: string; variant: "secondary" | "success" | "warning" | "default" | "outline" }
> = {
  draft: { label: "Draft", variant: "secondary" },
  active: { label: "Active", variant: "success" },
  closed: { label: "Closed", variant: "warning" },
  producing: { label: "Producing", variant: "default" },
  completed: { label: "Complete", variant: "outline" },
};

type StatusFilter = "all" | "draft" | "active" | "closed" | "completed";
type SortKey = "newest" | "oldest" | "participants" | "uploads";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "participants", label: "Most participants" },
  { key: "uploads", label: "Most uploads" },
];


const DEMO_MODE_KEY = "organize_demo_mode";

/** Read persisted demo mode state from localStorage */
function loadDemoMode(): { active: boolean; activatedAt: number | null } {
  try {
    const raw = localStorage.getItem(DEMO_MODE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.activatedAt === "number") {
        return { active: true, activatedAt: parsed.activatedAt };
      }
    }
  } catch {
    // ignore
  }
  return { active: false, activatedAt: null };
}

function EventCard({ event }: { event: MixEvent }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const config = statusConfig[event.status] || {
    label: event.status,
    variant: "secondary" as const,
  };

  const deleteMutation = useMutation({
    mutationFn: () => deleteEvent(event.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setDeleteOpen(false);
    },
    onError: (err) => {
      alert(err instanceof Error ? err.message : "Failed to delete event");
    },
  });

  return (
    <>
      <Card
        className="cursor-pointer hover:border-vizimix-600 transition-colors"
        onClick={() => navigate(`/events/${event.id}`)}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-lg font-semibold text-vizimix-100 truncate pr-3">
              {event.title}
            </h3>
            <div className="flex items-center gap-2 shrink-0">
              {event.eventType && event.eventType !== "general" && (
                <Badge variant="outline" className="capitalize">
                  {event.eventType}
                </Badge>
              )}
              <Badge variant={config.variant}>{config.label}</Badge>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteOpen(true);
                }}
                className="ml-1 rounded-md p-1 text-vizimix-500 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                aria-label="Delete event"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {event.description && (
            <p className="text-sm text-vizimix-400 mb-4 line-clamp-2">
              {event.description}
            </p>
          )}

          <div className="flex items-center gap-2 text-sm text-vizimix-400 mb-3">
            <CalendarDays className="h-4 w-4" />
            <span>{formatDateRange(event.startsAt, event.endsAt)}</span>
          </div>

          <div className="flex items-center gap-4 text-sm text-vizimix-400">
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              <span>{event.participantCount ?? 0} participants</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Video className="h-4 w-4" />
              <span>{event.uploadCount ?? 0} uploads</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete Event"
        description={`Are you sure you want to delete "${event.title}"? This will also remove all participants, uploads, and productions for this event. This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}

export function EventsDashboard() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [searchQuery, setSearchQuery] = useState("");

  // Demo mode: hide existing events and only show events created after activation
  const [demoMode, setDemoMode] = useState(loadDemoMode);

  const toggleDemoMode = useCallback(() => {
    setDemoMode((prev) => {
      if (prev.active) {
        localStorage.removeItem(DEMO_MODE_KEY);
        return { active: false, activatedAt: null };
      }
      const activatedAt = Date.now();
      localStorage.setItem(DEMO_MODE_KEY, JSON.stringify({ activatedAt }));
      return { active: true, activatedAt };
    });
  }, []);

  const {
    data: events,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["events"],
    queryFn: listEvents,
  });

  // Count events per status
  const statusCounts = useMemo(() => {
    if (!events) return { all: 0, draft: 0, active: 0, closed: 0, completed: 0 };
    return {
      all: events.length,
      draft: events.filter((e) => e.status === "draft").length,
      active: events.filter((e) => e.status === "active").length,
      closed: events.filter((e) => e.status === "closed" || e.status === "producing").length,
      completed: events.filter((e) => e.status === "completed").length,
    };
  }, [events]);

  // Filter and sort
  const filteredEvents = useMemo(() => {
    if (!events) return [];

    let filtered = events;

    // Demo mode filter: only show events created after activation
    if (demoMode.active && demoMode.activatedAt) {
      filtered = filtered.filter((e) => e.createdAt > demoMode.activatedAt!);
    }

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (e) =>
          e.title.toLowerCase().includes(q) ||
          (e.description && e.description.toLowerCase().includes(q)),
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "closed") {
        // Include "producing" under "closed" filter
        filtered = filtered.filter((e) => e.status === "closed" || e.status === "producing");
      } else {
        filtered = filtered.filter((e) => e.status === statusFilter);
      }
    }

    const sorted = [...filtered];
    switch (sortKey) {
      case "newest":
        sorted.sort((a, b) => b.createdAt - a.createdAt);
        break;
      case "oldest":
        sorted.sort((a, b) => a.createdAt - b.createdAt);
        break;
      case "participants":
        sorted.sort((a, b) => (b.participantCount ?? 0) - (a.participantCount ?? 0));
        break;
      case "uploads":
        sorted.sort((a, b) => (b.uploadCount ?? 0) - (a.uploadCount ?? 0));
        break;
    }

    return sorted;
  }, [events, statusFilter, sortKey, searchQuery, demoMode]);

  const filterTabs: { key: StatusFilter; label: string; count: number }[] = [
    { key: "all", label: "All", count: statusCounts.all },
    { key: "draft", label: "Draft", count: statusCounts.draft },
    { key: "active", label: "Active", count: statusCounts.active },
    { key: "closed", label: "Closed", count: statusCounts.closed },
    { key: "completed", label: "Complete", count: statusCounts.completed },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-vizimix-100">Events</h2>
          <p className="text-sm text-vizimix-400 mt-1">
            Create and manage your video mix events
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label
            className={cn(
              "flex items-center gap-2 cursor-pointer select-none rounded-md border px-3 py-2 text-sm transition-colors",
              demoMode.active
                ? "border-amber-500/50 bg-amber-500/10 text-amber-400"
                : "border-organize-border text-vizimix-400 hover:text-vizimix-200"
            )}
          >
            <input
              type="checkbox"
              checked={demoMode.active}
              onChange={toggleDemoMode}
              className="sr-only"
            />
            <Eye className="h-4 w-4" />
            Demo Mode
          </label>
          <Button onClick={() => navigate("/events/new")} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Event
          </Button>
        </div>
      </div>

      {/* Search */}
      {events && events.length > 0 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-vizimix-500" />
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-md border border-organize-border bg-organize-bg pl-10 pr-3 py-2 text-sm text-vizimix-100 placeholder:text-vizimix-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500 focus-visible:ring-offset-2 focus-visible:ring-offset-organize-bg"
          />
        </div>
      )}

      {/* Filter & Sort Controls */}
      {events && events.length > 0 && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-1 border-b border-organize-border">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={cn(
                  "px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px",
                  statusFilter === tab.key
                    ? "border-vizimix-500 text-vizimix-100"
                    : "border-transparent text-vizimix-400 hover:text-vizimix-200"
                )}
              >
                {tab.label}{" "}
                <span className="text-vizimix-500 ml-1">({tab.count})</span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <ArrowUpDown className="h-4 w-4 text-vizimix-400" />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              className="rounded-md border border-organize-border bg-organize-bg px-3 py-2 text-sm text-vizimix-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vizimix-500"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-vizimix-500" />
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 text-sm text-destructive">
          Failed to load events: {error instanceof Error ? error.message : "Unknown error"}
        </div>
      )}

      {events && events.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CalendarDays className="h-12 w-12 text-vizimix-600 mb-4" />
            <h3 className="text-lg font-semibold text-vizimix-200 mb-2">
              No events yet
            </h3>
            <p className="text-sm text-vizimix-400 mb-6 text-center max-w-sm">
              Create your first event to start collecting video clips from
              participants.
            </p>
            <Button onClick={() => navigate("/events/new")} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Event
            </Button>
          </CardContent>
        </Card>
      )}

      {filteredEvents.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}

      {events && events.length > 0 && filteredEvents.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            {searchQuery.trim() ? (
              <>
                <Search className="h-12 w-12 text-vizimix-600 mb-4" />
                <h3 className="text-lg font-semibold text-vizimix-200 mb-2">
                  No matching events
                </h3>
                <p className="text-sm text-vizimix-400">
                  No events match &ldquo;{searchQuery.trim()}&rdquo;. Try a different search term.
                </p>
              </>
            ) : (
              <>
                <CalendarDays className="h-12 w-12 text-vizimix-600 mb-4" />
                <h3 className="text-lg font-semibold text-vizimix-200 mb-2">
                  No {statusFilter} events
                </h3>
                <p className="text-sm text-vizimix-400">
                  Try a different filter to see your events.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
